import type { RumEvent } from '@datadog/browser-rum'

export const beforeSend = (log: Record<string, unknown>): boolean => {
  const http = log.http as { status_code?: number; url?: unknown } | undefined

  if (http?.status_code === 0) {
    return false
  }

  if (typeof http?.url === 'string' && isUrlBlacklisted(http.url)) {
    return false
  }

  if (typeof log?.message === 'string' && isMessageBlacklisted(log.message)) {
    return false
  }

  return true
}

export const beforeSendRum = (event: RumEvent): boolean => {
  if (event.type !== 'error') {
    return true
  }

  const resourceUrl = event.error.resource?.url
  const isNoise =
    isMessageBlacklisted(event.error.message) ||
    (typeof resourceUrl === 'string' && isUrlBlacklisted(resourceUrl))

  if (!isNoise) {
    return true
  }

  const occurrence = countSuppressed(`${event.session.id}|${event.error.message}`)

  if (!SAMPLE_AT.has(occurrence) && occurrence % 100_000 !== 0) {
    return false
  }

  event.context = { ...event.context, suppressedOccurrence: occurrence }

  return true
}

const urlBlackList = [
  'taboola',
  'madgicx',
  'google-analytics',
  'hotjar',
  'hyros',
  'doubleclick',
  'twitter',
  'pinterest',
  'facebook',
  'tracking.kilo.live',
  'clarity',
  'bing',
  'visualwebsiteoptimizer',
  'zdassets.com',
  'zendesk.com',
  'zopim.com',
]

const messageBlackList = [
  'ResizeObserver',
  'Loading chunk',
  'Network request failed',
  'Abort fetching component for route:',
  'Route did not complete loading:',
  /Fetch error GET .*\/_next\/data\//,
  'Failed to load static props',
  'Loading initial props cancelled',
  'Load failed',
  'Failed to load',
  'Network Error',
  'TypeError: Load failed',
  'Script error',
  'Object Not Found Matching Id:',
  'visualwebsiteoptimizer',
  'ResizeObserver loop completed with undelivered notifications.',
]

const SAMPLE_AT = new Set([1, 10, 100, 1_000, 10_000])

const suppressedCounts = new Map<string, number>()

function countSuppressed(key: string) {
  const occurrence = (suppressedCounts.get(key) ?? 0) + 1
  suppressedCounts.set(key, occurrence)

  return occurrence
}

function isLogBlacklisted(blackListItem: string | RegExp, entry: string) {
  if (typeof blackListItem === 'string') {
    return entry.includes(blackListItem)
  }

  return blackListItem.test(entry)
}

function isUrlBlacklisted(url: string) {
  return urlBlackList.some((blackListItem) => isLogBlacklisted(blackListItem, url))
}

function isMessageBlacklisted(message: string) {
  return messageBlackList.some((blackListItem) => isLogBlacklisted(blackListItem, message))
}
